import { useEffect, useRef } from 'react'
import { useGlobalEvent } from '@highlight/ipc/react'
import { GlobalState } from '@highlight/ipc'
import { useActiveChatId, useGetRealChatId } from '@renderer/chat/store'
import { MessageCircleQuestion, Zap } from 'lucide-react'
import { isAskUserQuestionStagedAction } from '@renderer/chat/integrations/ask-user-question'
import { isAskUserFeedbackStagedAction } from '@renderer/chat/integrations/ask-user-feedback'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { isPlannerV2Action, plannerVersion } from '@renderer/overlay/Sidepanel/hooks/staged-actions/confidence'
import { useOpenViewInSidepanel } from '@renderer/overlay/hooks/use-open-view-in-sidepanel'
import { useSidepanelState } from '@renderer/overlay/atoms'
import { getSidepanelTopView, useViewNavigation } from '@renderer/overlay/Sidepanel/view-atoms'
import { viewContextOf } from '@renderer/overlay/Sidepanel/view-definitions'
import type { StagedAction } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { ActionToast, ACTION_TOAST_DURATION_MS } from '../toasts/action-toast'
import { STAGED_ACTIONS_TOAST_TYPE } from '@renderer/overlay/Mini/track-notch'
import { NotchActionToast } from '../toasts/notch-action-toast'
import { toastManager } from '../toast-store'
import { useToast } from '../use-toast'
import { isOnboardingComplete } from '@shared/onboarding/onboarding-flags'
import {
  useAudioNoteAudioSessionId,
  useSavedAudioNoteId,
  useSummaryNoteId,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { isMeetingReference } from '@renderer/overlay/Sidepanel/views/meeting-view/action-items/use-meeting-actions'
import { useAudioNotePhase } from '@renderer/overlay/Mini/audio-note/use-audio-note-phase'
import { useIsMiniNotched } from '@renderer/overlay/Mini/atoms'

type ToastApi = ReturnType<typeof useToast>

export function useActionToasts(toast: ToastApi) {
  const { openViewInSidepanel } = useOpenViewInSidepanel()
  const actionBatchRef = useRef<Array<StagedAction>>([])
  const pendingAnalyticsRef = useRef(false)
  // Track live activeChatId via ref so the event callback (not a hook) can
  // read it without stale closure issues. HIG-5126.
  const liveActiveChatIdRef = useRef<string | undefined>(undefined)
  const liveActiveChatId = useActiveChatId()
  useEffect(() => {
    liveActiveChatIdRef.current = liveActiveChatId
  }, [liveActiveChatId])
  // The notch, while docked, lists the meeting's actions itself, from recording through its post-meeting view
  const notchMeetingRef = useRef<{ sessionId: string | null; noteId: string | null } | null>(null)
  // Main clears the session id on save, but the post-meeting view still lists that meeting's actions
  const lastSessionIdRef = useRef<string | null>(null)
  const isNotched = useIsMiniNotched()
  const liveSessionId = useAudioNoteAudioSessionId()
  if (liveSessionId) lastSessionIdRef.current = liveSessionId
  const savedNoteId = useSavedAudioNoteId()
  const summaryNoteId = useSummaryNoteId()
  const phase = useAudioNotePhase()
  const isNotchMeeting = phase === 'recording' || phase === 'saving' || phase === 'finished'
  notchMeetingRef.current =
    isNotched && isNotchMeeting
      ? // The note id only once this meeting ends: the last meeting's lingers into the next recording
        { sessionId: lastSessionIdRef.current, noteId: phase === 'recording' ? null : (savedNoteId ?? summaryNoteId) }
      : null

  useEffect(() => {
    const unsubscribe = toastManager.subscribe((toasts) => {
      const actionToast = toasts.find((t) => t.id === 'action-toast')
      if (!actionToast || actionToast.exiting) {
        actionBatchRef.current = []
      }
    })
    return () => unsubscribe()
  }, [])

  // Opening the chat the toast's actions came from renders them inline, making
  // the toast redundant — the same reason the emit-time gate below suppresses
  // it. Only when every batched action is from that chat: a mixed batch still
  // points at actions the user hasn't seen. HIG-6036.
  const sidepanelState = useSidepanelState()
  const { topView } = useViewNavigation()
  const getRealChatId = useGetRealChatId()
  useEffect(() => {
    if (sidepanelState !== 'open' || topView.id !== 'chat') return
    const batch = actionBatchRef.current
    if (batch.length === 0) return
    // The drawer's own context id is the chat actually rendered; resolve it
    // through the temp→real mapping to cover the ID transition window. Unioning
    // in liveActiveChatId instead would dismiss a toast for chat A while the
    // user is looking at chat B, since the two diverge whenever the atom lags a
    // view switch (or with parallel chats). It's only a fallback for a chat view
    // that carries no id yet.
    const contextChatId = viewContextOf(topView, 'chat')?.chatId
    const renderedChatId = contextChatId ?? liveActiveChatId
    if (!renderedChatId) return
    const openChatIds = new Set([renderedChatId, getRealChatId(renderedChatId)])
    if (!batch.every((action) => action.conversationId != null && openChatIds.has(action.conversationId))) return
    toastManager.dismiss('action-toast')
  }, [sidepanelState, topView, liveActiveChatId, getRealChatId])

  useGlobalEvent('staged-action:new', (action: StagedAction) => {
    // Feedback lives only in the actions list: nothing is waiting on it, and a
    // brief toast is not long enough to answer it. HIG-5967.
    if (isAskUserFeedbackStagedAction(action)) {
      return
    }

    // Archiving removes an action silently — it must never raise a toast, even
    // if an emitter upstream (or a stale snapshot) still thinks it's news.
    if (action.archivedAt != null) {
      return
    }

    const onboardedInUserStore = isOnboardingComplete()
    const quickTourActive = GlobalState.get<boolean>('sidepanel.quickTourActive', false)
    // Skip the toast only when this action's conversation is already rendered
    // inline in the active chat drawer AND the sidepanel is actually open. When
    // the sidepanel is hidden (e.g. the app is backgrounded) the inline action
    // isn't visible, so the toast must still fire — otherwise it stays suppressed
    // until drawer/visibility state changes much later. HIG-5126, HIG-5221.
    const sidepanelOpen = GlobalState.get<string>('sidepanel.state', 'hidden') === 'open'
    const topView = getSidepanelTopView()
    const isTopChatView = topView.id === 'chat'
    // Check both the (possibly stale) drawer context chatId and the live
    // activeChatId atom value to cover the temp→real ID transition window. HIG-5126.
    const contextChatId = viewContextOf(topView, 'chat')?.chatId
    const liveActiveChatId = isTopChatView ? liveActiveChatIdRef.current : undefined
    const inActiveChatView =
      (!!contextChatId && action.conversationId === contextChatId) ||
      (!!liveActiveChatId && action.conversationId === liveActiveChatId)
    if (!onboardedInUserStore || quickTourActive || (inActiveChatView && sidepanelOpen)) {
      return
    }

    const notchMeeting = notchMeetingRef.current
    if (
      notchMeeting &&
      isPlannerV2Action(action) &&
      action.references?.some((reference) => isMeetingReference(reference, notchMeeting.sessionId, notchMeeting.noteId))
    ) {
      return
    }
    showActionToast(action)
  })

  function showActionToast(action: StagedAction) {
    const isVisible = toastManager.getToasts().some((t) => t.id === 'action-toast' && !t.exiting)

    if (isVisible) {
      // Dedupe by id: the new-action detector runs per-window, so the same action
      // can arrive more than once via cross-window emits.
      actionBatchRef.current = actionBatchRef.current.some((a) => a.id === action.id)
        ? actionBatchRef.current
        : [...actionBatchRef.current, action]
    } else {
      actionBatchRef.current = [action]
    }

    const actions = actionBatchRef.current
    // A question isn't something Highlight did for you — it's Highlight asking
    // you for input. Give it its own framing instead of "N new actions".
    const allQuestions = actions.every((a) => isAskUserQuestionStagedAction(a))
    const title = allQuestions
      ? actions.length === 1
        ? 'Highlight has a question'
        : `Highlight has ${actions.length} questions`
      : actions.length === 1
        ? '1 new action'
        : `${actions.length} new actions`
    const icon = allQuestions ? (
      <MessageCircleQuestion className="size-3.5 opacity-70" />
    ) : (
      <Zap className="size-3.5 fill-current opacity-70" />
    )

    toast.add({
      id: 'action-toast',
      analytics: {
        type: STAGED_ACTIONS_TOAST_TYPE,
        actionCount: actions.length,
        actionIds: actions.map((a) => a.id),
        toolNames: actions.map((a) => a.toolName),
        toolSets: actions.map((a) => a.toolSet),
        planners: actions.map(plannerVersion),
        allQuestions,
      },
      icon,
      title: title.length > 40 ? title.slice(0, 40) + '…' : title,
      // ActionToast lowers this to ACTION_TOAST_SUCCESS_DURATION_MS once every
      // remaining action has succeeded.
      duration: ACTION_TOAST_DURATION_MS,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 8000,
      keepExpandedOnRefresh: true,
      stableHeaderKey: 'action-toast',
      // Outrank informational toasts (e.g. chat-complete) so a burst of those
      // can't evict the actionable toast under the MAX_VISIBLE cap. HIG-5126.
      priority: 1,
      onHeaderClick: () => {
        openViewInSidepanel('actions')
        toastManager.dismiss('action-toast')
      },
      body: (toastId) => <ActionToast actions={actions} toastId={toastId} />,
      notchBody: (toastId) => <NotchActionToast actions={actions} label={title} toastId={toastId} />,
    })

    if (!pendingAnalyticsRef.current) {
      pendingAnalyticsRef.current = true
      queueMicrotask(() => {
        pendingAnalyticsRef.current = false
        const currentActions = actionBatchRef.current
        AmplitudeRenderer.trackEvent('Staged Action', {
          action: 'toast shown',
          actionIds: currentActions.map((a) => a.id),
          count: currentActions.length,
          toolNames: currentActions.map((a) => a.toolName),
          toolSets: currentActions.map((a) => a.toolSet),
          planners: currentActions.map(plannerVersion),
        })
      })
    }
  }
}
